/* eslint-disable */
import { useMemo, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	InputAdornment,
	Checkbox,
} from '@mui/material';
import { Search } from 'lucide-react';

const MAX_ROWS = 300; // safety cap; refine search to narrow

const variantRow = (product, variant) => ({
	id: null,
	variantId: variant.id,
	category: '',
	price: null, // inherits variant.sellingPrice until overridden
	displayName: '',
	imageUrl: '', // never persist the product's presigned URL; upload via the item editor
	fulfilmentType: null,
	isEnabled: true,
	isAvailable: true,
	_productName: product?.productName,
	_variantName: variant?.displayName || variant?.size || '',
	_clientVariantId: variant?.clientVariantId || variant?.id,
	_sellingPrice: variant?.sellingPrice ?? variant?.price ?? null,
});

// Product -> variant picker. Single-variant products render as ONE flat row;
// multi-variant products render a product header (whose checkbox selects ALL its
// variants) with indented variant rows. Anything already on the menu is shown
// disabled and excluded from selection, so nothing is added twice.
const MenuItemSelection = (props) => {
	const {
		open,
		onClose,
		onAdd,
		targetCategory,
		productsList = [],
		existingVariantIds = [],
	} = props;

	const [search, setSearch] = useState('');
	const [selected, setSelected] = useState({}); // variantId -> {product, variant}

	const existingSet = useMemo(
		() => new Set((existingVariantIds || []).map((id) => Number(id))),
		[existingVariantIds]
	);

	// [{product, shown:[variants], single}] filtered by search, capped.
	const groups = useMemo(() => {
		const q = search.trim().toLowerCase();
		const out = [];
		let count = 0;
		for (const product of productsList || []) {
			if (product?.isEnabled === false) continue;
			const variants = (product?.variants || []).filter(
				(v) => !v?.isDeleted && !v?.isDisabled
			);
			if (!variants.length) continue;
			const prodMatch = !q || (product?.productName || '').toLowerCase().includes(q);
			const shown = prodMatch
				? variants
				: variants.filter((v) =>
						`${v?.displayName || ''} ${v?.clientVariantId || ''}`
							.toLowerCase()
							.includes(q)
				  );
			if (!shown.length) continue;
			out.push({ product, shown, single: variants.length === 1 });
			count += shown.length;
			if (count >= MAX_ROWS) break;
		}
		return out;
	}, [productsList, search]);

	const selectedCount = Object.keys(selected).length;

	const isExisting = (v) => existingSet.has(Number(v.id));

	const setVariant = (product, variant, on) =>
		setSelected((prev) => {
			const next = { ...prev };
			if (on) next[variant.id] = { product, variant };
			else delete next[variant.id];
			return next;
		});

	const toggleVariant = (product, variant) => {
		if (isExisting(variant)) return;
		setVariant(product, variant, !selected[variant.id]);
	};

	// product-level: acts on all still-addable (not-existing) shown variants
	const productState = (grp) => {
		const addable = grp.shown.filter((v) => !isExisting(v));
		const sel = addable.filter((v) => selected[v.id]);
		return {
			addable,
			allExisting: addable.length === 0,
			checked: addable.length > 0 && sel.length === addable.length,
			indeterminate: sel.length > 0 && sel.length < addable.length,
		};
	};

	const toggleProduct = (grp) => {
		const { addable, checked } = productState(grp);
		setSelected((prev) => {
			const next = { ...prev };
			addable.forEach((v) => {
				if (checked) delete next[v.id];
				else next[v.id] = { product: grp.product, variant: v };
			});
			return next;
		});
	};

	const handleAdd = () => {
		const items = Object.values(selected).map(({ product, variant }) =>
			variantRow(product, variant)
		);
		onAdd(items);
		setSelected({});
		setSearch('');
		onClose();
	};

	const close = () => {
		setSelected({});
		setSearch('');
		onClose();
	};

	const Thumb = ({ src, size = 28 }) =>
		src ? (
			<img src={src} alt='' style={{ width: size, height: size, borderRadius: 6, objectFit: 'cover' }} />
		) : (
			<div style={{ width: size, height: size, borderRadius: 6, background: '#e5e7eb' }} />
		);

	return (
		<Dialog open={open} onClose={close} maxWidth='sm' fullWidth>
			<DialogTitle sx={{ pb: 1 }}>
				<div style={{ fontSize: 16, fontWeight: 600 }}>Add items</div>
				<div style={{ fontSize: 12, color: '#6b7280' }}>
					pick product variants{targetCategory ? ` → ${targetCategory}` : ''}
				</div>
			</DialogTitle>
			<DialogContent dividers sx={{ p: 0 }}>
				<div style={{ padding: 12 }}>
					<TextField
						size='small'
						fullWidth
						autoFocus
						placeholder='Search products or variants…'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>
									<Search size={16} />
								</InputAdornment>
							),
						}}
					/>
				</div>
				<div style={{ maxHeight: 420, overflowY: 'auto' }}>
					{groups.length === 0 ? (
						<div style={{ padding: '48px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
							No products match your search.
						</div>
					) : (
						groups.map((grp) => {
							const { product, shown, single } = grp;

							// ---- single variant: one flat, product-level row ----
							if (single) {
								const variant = shown[0];
								const disabled = isExisting(variant);
								return (
									<div
										key={product.id}
										onClick={() => toggleVariant(product, variant)}
										role='button'
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 10,
											padding: '8px 14px',
											borderTop: '1px solid #f3f4f6',
											cursor: disabled ? 'default' : 'pointer',
											opacity: disabled ? 0.5 : 1,
										}}
									>
										<Checkbox size='small' checked={!!selected[variant.id]} disabled={disabled} sx={{ p: 0.5 }} />
										<Thumb src={product?.productImageUrl} />
										<div style={{ flex: 1, minWidth: 0 }}>
											<div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
												{product?.productName}
											</div>
											<div style={{ fontSize: 11, color: '#9ca3af' }}>
												variant #{variant?.clientVariantId || variant?.id}
												{disabled ? ' · already added' : ''}
											</div>
										</div>
										<div style={{ fontSize: 13, color: '#374151' }}>
											₹{variant?.sellingPrice ?? variant?.price ?? '-'}
										</div>
									</div>
								);
							}

							// ---- multi variant: product header (select-all) + rows ----
							const ps = productState(grp);
							return (
								<div key={product.id}>
									<div
										onClick={() => !ps.allExisting && toggleProduct(grp)}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 8,
											padding: '8px 14px',
											background: '#f9fafb',
											borderTop: '1px solid #f3f4f6',
											cursor: ps.allExisting ? 'default' : 'pointer',
										}}
									>
										<Checkbox
											size='small'
											checked={ps.checked}
											indeterminate={ps.indeterminate}
											disabled={ps.allExisting}
											sx={{ p: 0.5 }}
										/>
										<Thumb src={product?.productImageUrl} />
										<span style={{ fontSize: 13, fontWeight: 600, color: '#111827', flex: 1 }}>
											{product?.productName}
										</span>
										<span style={{ fontSize: 11, color: '#9ca3af' }}>
											{shown.length} variants
										</span>
									</div>
									{shown.map((variant) => {
										const disabled = isExisting(variant);
										return (
											<div
												key={variant.id}
												onClick={() => toggleVariant(product, variant)}
												role='button'
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: 8,
													padding: '6px 14px 6px 44px',
													borderTop: '1px solid #f9fafb',
													cursor: disabled ? 'default' : 'pointer',
													opacity: disabled ? 0.5 : 1,
												}}
											>
												<Checkbox size='small' checked={!!selected[variant.id]} disabled={disabled} sx={{ p: 0.5 }} />
												<div style={{ flex: 1, minWidth: 0 }}>
													<div style={{ fontSize: 13, color: '#1f2937' }}>
														{variant?.displayName || variant?.size || 'Default'}
													</div>
													<div style={{ fontSize: 11, color: '#9ca3af' }}>
														variant #{variant?.clientVariantId || variant?.id}
														{disabled ? ' · already added' : ''}
													</div>
												</div>
												<div style={{ fontSize: 13, color: '#374151' }}>
													₹{variant?.sellingPrice ?? variant?.price ?? '-'}
												</div>
											</div>
										);
									})}
								</div>
							);
						})
					)}
				</div>
			</DialogContent>
			<DialogActions>
				<Button onClick={close} color='inherit'>
					Cancel
				</Button>
				<Button variant='contained' disabled={selectedCount === 0} onClick={handleAdd}>
					Add{selectedCount ? ` ${selectedCount}` : ''}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default MenuItemSelection;
